import { FaTruck, FaClock, FaShieldAlt, FaChartLine, FaCheckCircle, FaArrowRight } from 'react-icons/fa'
import { useEffect, useState, useRef } from 'react'
import { getStats } from '../utils/api'
import { scrollToSection } from '../utils/scroll'
import TextPressure from '../components/TextPressure'
import CountUp from '../components/CountUp'
import VariableProximity from '../components/VariableProximity'
import GlassSurface from '../components/GlassSurface'

const Home = () => {
  const [stats, setStats] = useState(null)
  const comprehensiveSolutionsRef = useRef(null)
  const whyChooseRef = useRef(null)
  const readyToGrowRef = useRef(null)

  useEffect(() => {
    getStats().then((data) => {
      if (data.success) {
        setStats(data.stats)
      }
    })
  }, [])

  const features = [
    {
      icon: <FaTruck className="text-4xl text-accent-400" />,
      title: 'Custom Scheduling',
      description: 'We organize your week according to your preferences, ensuring consistent loads',
    },
    {
      icon: <FaClock className="text-4xl text-accent-400" />,
      title: '24/7 Dispatch Support',
      description: 'Assistance is always available, day or night',
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent-400" />,
      title: 'Streamlined Paperwork',
      description: 'We handle everything from broker accounts to insurance certificates and permits',
    },
    {
      icon: <FaChartLine className="text-4xl text-accent-400" />,
      title: 'Maximized Earnings',
      description: 'We negotiate the best-paying rates to ensure your desired gross is met',
    },
  ]

  const whyChooseUs = [
    'Custom weekly scheduling organized according to your preferences',
    'Best-paying rates negotiated to meet your desired gross',
    'Complete paperwork handling from broker accounts to permits',
    'Personal dispatcher assigned to monitor your calendar and preferences',
    'Assistance with TONU, Lumper, Detention, and Layover costs',
    'No force dispatch - you remain in full control of your schedule',
    '24/7 dispatch support always available',
    'No contract obligations - free to discontinue at any time',
  ]

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative overflow-hidden text-white pt-32 pb-24 min-h-[700px] md:min-h-[800px] flex items-center"
        style={{ backgroundColor: '#0f172a' }}
      >
        {/* Background video from public/ (replace /truck.mp4 if needed) */}
        <div className="absolute inset-0 overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full"
            autoPlay
            loop
            muted
            playsInline
            style={{
              objectFit: 'cover',
              objectPosition: 'center center',
              width: '100%',
              height: '100%',
              minWidth: '100%',
              minHeight: '100%',
              transform: 'scale(1.2)',
            }}
          >
            <source src="/truck.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-black/45 z-[1]"></div>
        
        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="w-full">
            <div className="h-36 md:h-48 lg:h-56 xl:h-64 mb-6 md:mb-8 w-full">
              <TextPressure
                text="Freight Consultancy Services"
                flex
                alpha={false}
                stroke={false}
                width
                weight
                italic
                textColor="#ffffff"
                minFontSize={80}
                className="justify-start w-full"
              />
            </div>
            <p className="text-xl md:text-2xl lg:text-3xl text-gray-100 mb-8 md:mb-10 font-medium">
              Alleviating the Pressure of Load Sourcing. Keeping Your Vehicle Moving with Competitive Rates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={() => scrollToSection('contact')} 
                className="relative text-lg inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 hover:scale-105 backdrop-blur-md bg-white/20 border border-white/30 shadow-lg hover:bg-white/25"
              >
                <span className="flex items-center">
                  Get Started Today
                  <FaArrowRight className="ml-2" />
                </span>
              </button>
              <button 
                onClick={() => scrollToSection('specialities')} 
                className="relative text-lg inline-flex items-center justify-center px-6 py-3 rounded-lg font-semibold text-white backdrop-blur-md bg-white/15 border-2 border-white/40 transition-all duration-200 hover:scale-105 hover:bg-white/20 shadow-lg"
              >
                View Services
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-dark-900 via-dark-800 to-transparent"></div>
      </section>

      {/* Stats Bar (post-hero) */}
      <section className="relative py-10 border-y border-dark-600/30 overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <GlassSurface
              width="100%"
              borderRadius={16}
              blur={8}
              displace={0.3}
              distortionScale={-100}
              redOffset={-2}
              greenOffset={6}
              blueOffset={12}
              backgroundOpacity={0.08}
              saturation={1.15}
              opacity={0.85}
              mixBlendMode="screen"
              className="transition-all hover:scale-105"
              style={{ minHeight: '160px' }}
            >
              <div className="text-center px-6 py-8 text-white h-full flex flex-col justify-center">
              <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                <CountUp from={0} to={stats?.active_trucks || 2} duration={2} separator="" />+
              </div>
              <div className="text-lg md:text-xl text-gray-300">Active Trucks</div>
            </div>
            </GlassSurface>
            <GlassSurface
              width="100%"
              borderRadius={16}
              blur={8}
              displace={0.3}
              distortionScale={-100}
              redOffset={-2}
              greenOffset={6}
              blueOffset={12}
              backgroundOpacity={0.08}
              saturation={1.15}
              opacity={0.85}
              mixBlendMode="screen"
              className="transition-all hover:scale-105"
              style={{ minHeight: '160px' }}
            >
              <div className="text-center px-6 py-8 text-white h-full flex flex-col justify-center">
              <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                <CountUp from={0} to={stats?.satisfaction_rate || 98} duration={2} separator="" />%
              </div>
              <div className="text-lg md:text-xl text-gray-300">Customer Satisfaction</div>
            </div>
            </GlassSurface>
            <GlassSurface
              width="100%"
              borderRadius={16}
              blur={8}
              displace={0.3}
              distortionScale={-100}
              redOffset={-2}
              greenOffset={6}
              blueOffset={12}
              backgroundOpacity={0.08}
              saturation={1.15}
              opacity={0.85}
              mixBlendMode="screen"
              className="transition-all hover:scale-105"
              style={{ minHeight: '160px' }}
            >
              <div className="text-center px-6 py-8 text-white h-full flex flex-col justify-center">
              <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">24/7</div>
              <div className="text-lg md:text-xl text-gray-300">Support Available</div>
            </div>
            </GlassSurface>
            <GlassSurface
              width="100%"
              borderRadius={16}
              blur={8}
              displace={0.3}
              distortionScale={-100}
              redOffset={-2}
              greenOffset={6}
              blueOffset={12}
              backgroundOpacity={0.08}
              saturation={1.15}
              opacity={0.85}
              mixBlendMode="screen"
              className="transition-all hover:scale-105"
              style={{ minHeight: '160px' }}
            >
              <div className="text-center px-6 py-8 text-white h-full flex flex-col justify-center">
              <div className="text-4xl md:text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                <CountUp from={0} to={stats?.years_experience || 5} duration={2} separator="" />+
              </div>
              <div className="text-lg md:text-xl text-gray-300">Years Experience</div>
            </div>
            </GlassSurface>
          </div>
        </div>
      </section>

      {/* Dispatch Service Overview */}
      <section className="section-padding relative overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div
              ref={comprehensiveSolutionsRef}
              style={{ position: 'relative' }}
              className="mb-4"
            >
              <VariableProximity
                label="Our Freight Consultancy Services"
                className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={comprehensiveSolutionsRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              We specialize in Freight Consultancy and strive to provide the best possible services to our carriers, 
              ensuring your vehicle is continuously moving with the most competitive rates in the industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <GlassSurface
                key={index}
                width="100%"
                borderRadius={16}
                blur={8}
                displace={0.3}
                distortionScale={-100}
                redOffset={-2}
                greenOffset={6}
                blueOffset={12}
                backgroundOpacity={0.08}
                saturation={1.15}
                opacity={0.85}
                mixBlendMode="screen"
                className="transition-all duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer group"
                style={{ minHeight: '200px' }}
              >
                <div className="p-6 h-full flex flex-col">
                <div className="mb-4 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">{feature.icon}</div>
                <h3 className="text-xl font-semibold text-white mb-2 transition-colors duration-300 group-hover:text-accent-400">
                  {feature.title}
                </h3>
                <p className="text-gray-300 transition-colors duration-300 group-hover:text-gray-200">{feature.description}</p>
              </div>
              </GlassSurface>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding relative overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div
                ref={whyChooseRef}
                style={{ position: 'relative' }}
                className="mb-6"
              >
                <VariableProximity
                  label="Why Choose EW Logistics & IT Solution LLC?"
                  className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                  fromFontVariationSettings="'wght' 400, 'opsz' 9"
                  toFontVariationSettings="'wght' 1000, 'opsz' 40"
                  containerRef={whyChooseRef}
                  radius={100}
                  falloff="linear"
                />
              </div>
              <p className="text-lg text-gray-300 mb-8">
                We are not just another freight consultancy firm. We are your strategic partner in building a successful trucking business. 
                Our focus is on alleviating the pressure of load sourcing and negotiations, ensuring that your vehicle is continuously 
                moving with the most competitive rates in the industry.
              </p>
              <ul className="space-y-4">
                {whyChooseUs.map((item, index) => (
                  <li key={index}>
                    <div className="bullet">
                      <FaCheckCircle 
                        className="text-accent-400 flex-shrink-0" 
                        size={18}
                      />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => scrollToSection('about')}
                className="mt-8 btn-primary"
              >
                <span className="flex items-center">
                  Learn More About Us
                  <FaArrowRight className="ml-2" />
                </span>
              </button>
            </div>
            <div className="relative">
              <div className="rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-6">
                  <GlassSurface
                    width="100%"
                    borderRadius={16}
                    blur={8}
                    displace={0.3}
                    distortionScale={-100}
                    redOffset={-2}
                    greenOffset={6}
                    blueOffset={12}
                    backgroundOpacity={0.08}
                    saturation={1.15}
                    opacity={0.85}
                    mixBlendMode="screen"
                    className="transition-all hover:scale-105"
                    style={{ minHeight: '120px' }}
                  >
                    <div className="text-white h-full flex flex-col justify-center items-center text-center px-4 py-6">
                    <div className="text-4xl font-bold mb-2">
                      <CountUp from={0} to={stats?.years_experience || 5} duration={2} separator="" />+
                    </div>
                    <div className="text-gray-200">Years Experience</div>
                  </div>
                  </GlassSurface>
                  <GlassSurface
                    width="100%"
                    borderRadius={16}
                    blur={8}
                    displace={0.3}
                    distortionScale={-100}
                    redOffset={-2}
                    greenOffset={6}
                    blueOffset={12}
                    backgroundOpacity={0.08}
                    saturation={1.15}
                    opacity={0.85}
                    mixBlendMode="screen"
                    className="transition-all hover:scale-105"
                    style={{ minHeight: '120px' }}
                  >
                    <div className="text-white h-full flex flex-col justify-center items-center text-center px-4 py-6">
                    <div className="text-4xl font-bold mb-2">
                      <CountUp from={0} to={stats?.happy_clients || 2} duration={2} separator="" />+
                    </div>
                    <div className="text-gray-200">Happy Clients</div>
                  </div>
                  </GlassSurface>
                  <GlassSurface
                    width="100%"
                    borderRadius={16}
                    blur={8}
                    displace={0.3}
                    distortionScale={-100}
                    redOffset={-2}
                    greenOffset={6}
                    blueOffset={12}
                    backgroundOpacity={0.08}
                    saturation={1.15}
                    opacity={0.85}
                    mixBlendMode="screen"
                    className="transition-all hover:scale-105"
                    style={{ minHeight: '120px' }}
                  >
                    <div className="text-white h-full flex flex-col justify-center items-center text-center px-4 py-6">
                    <div className="text-4xl font-bold mb-2">
                      {stats?.loads_dispatched ? (
                        <>
                          <CountUp from={0} to={Math.floor((stats.loads_dispatched / 1000))} duration={2} separator="" />K+
                        </>
                      ) : (
                        '50K+'
                      )}
                    </div>
                    <div className="text-gray-200">Loads Dispatched</div>
                  </div>
                  </GlassSurface>
                  <GlassSurface
                    width="100%"
                    borderRadius={16}
                    blur={8}
                    displace={0.3}
                    distortionScale={-100}
                    redOffset={-2}
                    greenOffset={6}
                    blueOffset={12}
                    backgroundOpacity={0.08}
                    saturation={1.15}
                    opacity={0.85}
                    mixBlendMode="screen"
                    className="transition-all hover:scale-105"
                    style={{ minHeight: '120px' }}
                  >
                    <div className="text-white h-full flex flex-col justify-center items-center text-center px-4 py-6">
                    <div className="text-4xl font-bold mb-2">
                      <CountUp from={0} to={stats?.satisfaction_rate || 98} duration={2} separator="" />%
                    </div>
                    <div className="text-gray-200">Satisfaction Rate</div>
                  </div>
                  </GlassSurface>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding relative text-white border-y border-dark-600/30 overflow-hidden">
        <div className="container-custom">
          <div 
            className="text-center rounded-3xl p-8 sm:p-12 max-w-4xl mx-auto"
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(12px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div
              ref={readyToGrowRef}
              style={{ position: 'relative' }}
              className="mb-4"
            >
              <VariableProximity
                label="Ready to Grow Your Trucking Business?"
                className="text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-white bg-clip-text text-transparent"
                fromFontVariationSettings="'wght' 400, 'opsz' 9"
                toFontVariationSettings="'wght' 1000, 'opsz' 40"
                containerRef={readyToGrowRef}
                radius={100}
                falloff="linear"
              />
            </div>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              We would be thrilled to collaborate with you and look forward to the possibility of working together. 
              Join carriers who trust EW Logistics & IT Solution LLC for their freight consultancy needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => scrollToSection('contact')} className="btn-primary">
                <span className="flex items-center">
                  Get Free Quote
                  <FaArrowRight className="ml-2" />
                </span>
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="btn-secondary">
                <span className="flex items-center">
                  Read Success Stories
                  <FaArrowRight className="ml-2" />
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home

